/**
 * A configuração da interface, lida em tempo de EXECUÇÃO.
 *
 * ── Por que não `import.meta.env` ─────────────────────────────────────────
 *
 * Porque o Vite embute o valor de `VITE_*` no bundle durante o build. Isso
 * significa que o pacote de staging e o de produção seriam binários
 * diferentes — e a §16.2 é explícita ao proibir:
 *
 *   > "promoção do mesmo artefato, sem rebuild. Rebuild por ambiente significa
 *   > que o que foi testado não é o que foi para produção — e o bug que aparece
 *   > só em produção passa a ser inexplicável."
 *
 * Com a configuração embutida, promover não é promover: é reconstruir e torcer.
 *
 * ── Como ela chega, então ─────────────────────────────────────────────────
 *
 * Por um `config.js` servido ao lado do `index.html`, carregado ANTES do
 * bundle. Ele só atribui um objeto a `window`. No desenvolvimento é o arquivo
 * de `public/`, com os endereços do laboratório; no contêiner, o entrypoint o
 * reescreve a partir das variáveis do ambiente antes de o servidor subir.
 *
 * O mesmo bundle, três ambientes, um arquivo de dez linhas mudando entre eles.
 *
 * ── Nada aqui é segredo, e isso não é descuido ────────────────────────────
 *
 * Tudo neste objeto chega ao navegador e é legível por qualquer pessoa que
 * abra as ferramentas de desenvolvedor. O client OIDC é público de propósito
 * (`publicClient: true` no realm), e quem faz o papel do segredo é o PKCE.
 *
 * A regra prática, que vale para quem for acrescentar campo aqui: se você
 * hesitaria em colar o valor num chamado de suporte, ele não pode entrar.
 */

/** O que o `config.js` precisa fornecer. */
export interface ConfiguracaoDaInterface {
  /** Base do Keycloak, SEM o realm — o realm vem da revenda (ADR-0006). */
  readonly oidcBase: string;
  /** O client público do realm. */
  readonly oidcClient: string;
  /**
   * O aplicativo da Meta, para o popup do WhatsApp (G02).
   *
   * ── Por que OPCIONAIS, ao contrário de `oidcBase` ───────────────────────
   *
   * Uma instalação sem WhatsApp é legítima. Exigi-los faria a interface
   * recusar-se a subir por causa de um canal que ninguém ia usar naquele
   * ambiente — e `config.sh` recusa subir justamente para não deixar a tela
   * quebrar no clique. Aqui a resposta certa é o contrário: sem eles, a tela
   * NÃO oferece o botão, e diz por quê.
   *
   * ── Por que podem ficar aqui, à vista ───────────────────────────────────
   *
   * Pela regra do cabeçalho deste arquivo. O `appId` já viaja no HTML de
   * qualquer página que carregue o SDK da Meta — o próprio motor de conversa o
   * imprime. E a configuração identifica um FLUXO de consentimento, não uma
   * credencial: sozinha, ela não autoriza nada.
   *
   * O que é segredo é o app secret, e ele nunca sai do servidor: é lá que o
   * `code` do popup vira token.
   */
  readonly metaAppId?: string;
  readonly metaConfiguracaoDoWhatsapp?: string;
}

declare global {
  interface Window {
    __CLIQCRM__?: Partial<ConfiguracaoDaInterface>;
  }
}

/**
 * Lê a configuração, ou explica o que falta.
 *
 * Preguiçosa de propósito: lida no import, ela quebraria todo teste de
 * componente que não montasse o `window` antes — e o erro apareceria longe da
 * causa, num arquivo que não fala de configuração.
 */
export function configuracaoDaInterface(): ConfiguracaoDaInterface {
  const bruta = globalThis.window?.__CLIQCRM__;

  if (!bruta?.oidcBase) {
    throw new Error(
      'A configuração da interface não chegou: `window.__CLIQCRM__.oidcBase` está vazia.\n\n' +
        'Ela vem de /config.js, que é carregado antes do bundle. No desenvolvimento o ' +
        'arquivo está em apps/web/public/config.js; no contêiner, o entrypoint o escreve ' +
        'a partir de OIDC_BASE.\n\n' +
        'Se você está vendo isto no navegador, /config.js não foi servido ou falhou ao ' +
        'carregar — confira a aba de rede.',
    );
  }

  return {
    oidcBase: bruta.oidcBase,
    // O client tem padrão porque ele quase nunca muda: é o mesmo nome em todo
    // realm que `packages/modulos/acesso/src/adaptadores/keycloak/realm.ts` cria. A base NÃO tem, porque um
    // padrão ali apontaria silenciosamente para o Keycloak errado.
    oidcClient: bruta.oidcClient ?? 'cliqcrm-web',
  };
}

/**
 * O aplicativo da Meta, ou `null` quando este ambiente não tem um.
 *
 * ── Por que não sai de `configuracaoDaInterface` ────────────────────────
 *
 * Porque aquela função ESTOURA sem `oidcBase`, e deve mesmo: sem provedor de
 * identidade não há sessão, e um padrão silencioso ali apontaria a produção
 * para o Keycloak errado.
 *
 * Só que a tela de conexões não precisa de `oidcBase` para se desenhar. Passar
 * por lá acopla uma seção da administração a uma configuração que não é dela —
 * e o sintoma foi exatamente esse: a tela inteira deixou de renderizar num
 * ambiente onde só faltava o Keycloak, e o erro falava de OIDC numa tela que
 * fala de WhatsApp.
 *
 * ── Os dois, ou nenhum ────────────────────────────────────────────────────
 *
 * Com só um deles preenchido, o botão apareceria e a Meta recusaria o popup
 * com um erro sobre configuração do aplicativo — longe daqui, e sem dizer qual
 * dos dois falta. Exigir o par transforma isso em "o canal não está
 * configurado neste ambiente", que é a verdade e é acionável.
 */
export function aplicativoDaMeta(): {
  readonly appId: string;
  readonly configuracao: string;
} | null {
  const bruta = globalThis.window?.__CLIQCRM__;
  const appId = bruta?.metaAppId;
  const configuracao = bruta?.metaConfiguracaoDoWhatsapp;
  if (!appId || !configuracao) return null;
  return { appId, configuracao };
}
